import { ReactElement, useCallback, useEffect, useMemo } from 'react';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { PracticeResearchCrumbs } from 'practice/constants/practice-crubs';
import { TypeRegister } from 'practice/constants/type-register';
import { useEventCallback } from '@mui/material';
import {
  createSearchParams,
  generatePath,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import SipoPersonForm from 'shell/components/shared/sipo-person-form';
import PmiTitle from 'shell/components/shared/title';
import {
  __PERSON_API_URL__,
  __PERSON_NEW_API_URL__,
  __PRACTICE_API_URL__,
} from 'shell/constants/environment';
import { PracticeData } from 'shell/models/practice';
import { ResidenceOrigin } from 'shell/view-models/personal-data';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { NewPersonVM } from 'shell/components/shared/sipo-person-form/new-person';
import { RegisterOfficeVM } from 'shell/view-models/register-office-practice-vm';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponse } from 'shell/models/http-response';

const NewPerson = (): ReactElement => {
  const params = useParams();

  const navigate = useNavigate();

  const [queryParams] = useSearchParams();

  const stateRequestApplicant = useLocation().state as {
    accountHolderFiscalCode: string | undefined;
  };

  // id intestatrio se presente fare get perchè siamo in modifica
  const idIntestatario = queryParams.get('idIntestatario') || '';
  const idAnpr = queryParams.get('idAnpr') || '';

  const isEdit = useMemo(() => {
    if ((idIntestatario && idIntestatario !== '0') || (idAnpr && idAnpr !== '0')) {
      return true;
    }

    return false;
  }, [idIntestatario, idAnpr]);

  const handleOnSuccess = useEventCallback((newIdIntestatario?: string) => {
    if (isEdit) {
      navigate(
        {
          pathname: generatePath(
            '/pratiche/ricerca/modifica/:codiceEnte/:tipoRegistro/:idAnnoPratica/:progressivoPratica/:tipoPratica',
            {
              codiceEnte: params.codiceEnte,
              tipoRegistro: params.tipoRegistro,
              idAnnoPratica: params.idAnnoPratica?.toString(),
              progressivoPratica: params.progressivoPratica?.toString(),
              tipoPratica: params.tipoPratica,
            }
          ),
          search: `?${createSearchParams({
            idIntestatario: newIdIntestatario || idIntestatario || '',
            idAnpr: idAnpr || '',
          })}`,
        },
        {
          state: stateRequestApplicant,
        }
      );

      return;
    }

    navigate(
      {
        pathname: generatePath(
          '/pratiche/ricerca/modifica/:codiceEnte/:tipoRegistro/:idAnnoPratica/:progressivoPratica/:tipoPratica',
          {
            codiceEnte: params.codiceEnte,
            tipoRegistro: params.tipoRegistro,
            idAnnoPratica: params.idAnnoPratica?.toString(),
            progressivoPratica: params.progressivoPratica?.toString(),
            tipoPratica: params.tipoPratica,
          }
        ),
        search: `?${createSearchParams({
          idIntestatario: newIdIntestatario || '',
        })}`,
      },
      {
        state: stateRequestApplicant,
      }
    );
  });

  const handleExit = useCallback(() => {
    navigate(
      {
        pathname: generatePath(
          '/pratiche/ricerca/modifica/:codiceEnte/:tipoRegistro/:idAnnoPratica/:progressivoPratica/:tipoPratica',
          {
            codiceEnte: params.codiceEnte,
            tipoRegistro: params.tipoRegistro,
            idAnnoPratica: params.idAnnoPratica?.toString(),
            progressivoPratica: params.progressivoPratica?.toString(),
            tipoPratica: params.tipoPratica,
          }
        ),
      },
      {
        state: stateRequestApplicant,
      }
    );
  }, [stateRequestApplicant]);

  const filterTable = useFilterTable<RegisterOfficeVM & NewPersonVM>();

  // eslint-disable-next-line no-console
  console.log('filterTable.values: ', filterTable.values);

  const [residenceOrigin, getResidenceOrigin] = useHttpFetch<
    HttpResponse<PracticeData>,
    ResidenceOrigin
  >(`${__PRACTICE_API_URL__}/pratiche/persone/provenienza`, {
    options: {
      errorCatching: true,
      method: 'POST',
      loading: true,
      data: {
        codiceEnte: params.codiceEnte,
        tipoRegistro: params.tipoRegistro,
        idAnnoPratica: params.idAnnoPratica ? Number.parseInt(params.idAnnoPratica, 10) : undefined,
        progressivoPratica: params.progressivoPratica
          ? Number.parseInt(params.progressivoPratica, 10)
          : undefined,
        idAnprPersona: idAnpr,
        idDbLocalePersona: idIntestatario ? Number.parseInt(idIntestatario, 10) : undefined,
      },
    },
    transformer: (response) => {
      const data: PracticeData = response.data.payload || {};

      return {
        irreperibile: data?.irreperibile,
        neverRegistered: data?.minorePreadozione,
        isForeign: !!data?.localita,
        origin: data?.comune
          ? {
              id: data?.comune?.codiceIstat || '',
              description: data?.comune?.nomeComune || '',
              province: data?.comune?.siglaProvinciaIstat || '',
            }
          : undefined,
        originState: data?.localita
          ? {
              id: data?.localita?.codiceStato || '',
              value: data?.localita?.descrizioneStato || '',
              province: data?.localita?.provinciaContea || '',
            }
          : undefined,
        originMunicipalityForeign: data?.localita
          ? {
              id: data?.localita?.codiceStato || '',
              description: data?.localita?.descrizioneLocalita || '',
              province: data?.localita?.provinciaContea || '',
            }
          : undefined,
        previousOrigin: data?.comunePrecedente?.descrizione,
        previousOriginProvince: data?.comunePrecedente?.provincia,
        previousOriginSipoCode: data?.comunePrecedente?.codice,
        previousOriginCode: data?.comunePrecedente?.codice,
      };
    },
  });

  useEffect(() => {
    if (isEdit && filterTable.values?.currentStep === 1 && filterTable.values?.fromList) {
      getResidenceOrigin();
    }
  }, [isEdit, filterTable.values?.currentStep]);

  return (
    <>
      <Breadcrumbs
        items={[
          ...PracticeResearchCrumbs,
          {
            name: 'Modifica pratica',
            path: generatePath(
              '/pratiche/ricerca/modifica/:codiceEnte/:tipoRegistro/:idAnnoPratica/:progressivoPratica/:tipoPratica',
              {
                codiceEnte: params.codiceEnte,
                tipoRegistro: params.tipoRegistro,
                idAnnoPratica: params.idAnnoPratica?.toString(),
                progressivoPratica: params.progressivoPratica?.toString(),
                tipoPratica: params.tipoPratica,
              }
            ),
          },
          {
            name: isEdit ? 'Modifica persona' : 'Nuova persona',
          },
        ]}
      />
      <PmiTitle title={isEdit ? 'Modifica persona' : 'Nuova persona'} />
      <SipoPersonForm
        onExit={handleExit}
        onSuccess={handleOnSuccess}
        urlGetData={
          idIntestatario && idIntestatario !== '0'
            ? `${__PERSON_API_URL__}/persona/pratiche/sipo/soggetto/${idIntestatario}`
            : `${__PERSON_NEW_API_URL__}/persona`
        }
        residenceOrigin={residenceOrigin}
        fiscalCode={stateRequestApplicant?.accountHolderFiscalCode}
        skipBirthActPartCheck={params.tipoRegistro === TypeRegister.RegisterOfficeTemporary}
      />
    </>
  );
};

NewPerson.displayName = 'Practice Edit Page component';

export default NewPerson;
